/**
 * RoomLandingTile — the rich, alive landing tile for ONE room the caller belongs to
 * (the "My Rooms" landing; sibling of NetworkStatTile).
 *
 * This view is for users assigned to ROOMS — room owners/supervisors/viewers who may
 * NOT be network members. It's the room-scoped analog of the networks landing: a quick
 * overview instead of the full room. The primary CTA is **"View N executions"** (the
 * room's run history) rather than "View N rooms"; N is the room-stats-lake ``runsTotal``.
 *
 * Data is one merged row from ``getMyRoomsVitals`` — the membership row (name, role,
 * lifecycle) enriched with vitals (runs, success, consumption). Absent vitals simply
 * don't render (a brand-new room shows "no runs", not zeros).
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { History, Users, Server, ChevronDown } from 'lucide-react';
import {
  SuccessRing, ConsumptionMeter, OutcomeChip,
} from '@stepflow/ui-shared/components/dashboard/vitalWidgets.jsx';
import RoomAvatar from '@stepflow/ui-shared/components/dashboard/RoomAvatar.jsx';
import { roomVisual } from '@stepflow/ui-shared/components/dashboard/RoomAvatar.jsx';

// Room role → a short, human chip. OWNER/CREATOR share a look (CREATOR is legacy OWNER).
const ROLE_LABEL = {
  OWNER: 'Owner', CREATOR: 'Owner', BUILDER: 'Builder',
  SUPERVISOR: 'Supervisor', VIEWER: 'Viewer',
};

export default function RoomLandingTile({ room, onOpen, onToggleExecutions, expanded }) {
  const r = room || {};
  const runs = Number(r.runsTotal || 0);
  const [hovered, setHovered] = useState(false);
  // Identity border: a soft tint of the ROOM's hue (which itself derives from the
  // parent network's hue ±15° — lineage), matching the avatar. Same treatment as
  // NetworkStatTile so the two landings read as one family.
  const roomHue = roomVisual(r.networkId, r.poolId).hue;
  const identityStyle = { borderColor: `hsl(${roomHue} 55% ${hovered ? 68 : 82}%)` };
  const roleLabel = ROLE_LABEL[String(r.role || '').toUpperCase()] || '';
  // Primary label — "Network name (room name)", matching Recently Visited. The
  // network name gives context; the room name (pool) is its own pet name. Falls
  // back to the network id when the friendly name isn't loaded.
  const roomName = r.name || r.poolId;
  const networkName = r.networkName || r.networkId;
  const primaryLabel = `${networkName} (${roomName})`;
  // Second deck — the FULL canonical room id (networkId-poolId), the durable handle.
  const fullRoomId = `${r.networkId}-${r.poolId}`;

  return (
    <motion.div
      layout
      style={identityStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative rounded-xl border bg-white p-3 transition-colors"
      data-testid={`room-landing-tile-${r.networkId}-${r.poolId}`}
    >
      {/* Header: room avatar (+ live pulse) + name/role, click → open the room. */}
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen?.(r.networkId, r.poolId)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          data-testid={`room-landing-open-${r.networkId}-${r.poolId}`}
        >
          <span className="relative shrink-0">
            <RoomAvatar networkId={r.networkId} poolId={r.poolId} name={r.name} size={30} />
            {r.isActive ? (
              <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 animate-pulse rounded-full border-2 border-white bg-emerald-500" aria-hidden />
            ) : null}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="min-w-0 truncate text-sm font-semibold text-gray-900" title={primaryLabel}>
                {primaryLabel}
              </span>
              {r.isActive ? (
                <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-emerald-600">
                  live
                </span>
              ) : null}
            </span>
            <span className="mt-0.5 flex items-center gap-1.5 leading-tight">
              {roleLabel ? (
                <span className="shrink-0 rounded bg-gray-100 px-1 py-px text-[9px] font-medium text-gray-500"
                      title={`Your role in this room: ${roleLabel}`}>
                  {roleLabel}
                </span>
              ) : null}
              <span className="min-w-0 truncate font-mono text-[10px] text-gray-400" title={fullRoomId}>
                {fullRoomId}
              </span>
              {r.stepflowVersion ? (
                <span className="shrink-0 rounded bg-gray-100 px-1 py-px font-mono text-[9px] text-gray-500"
                      title={`Release ${r.stepflowVersion}`}>
                  v{r.stepflowVersion}
                </span>
              ) : null}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          <OutcomeChip pct={r.outcomePct} />
        </div>
      </div>

      {/* Vitals: agents · success ring + consumption. (No rooms/active-rooms — this
          IS a room; "active" is the live pulse above.) */}
      <div className="mt-2.5 flex items-center gap-3">
        <div className="flex items-center gap-1.5" title="Success rate (lifetime)">
          <SuccessRing pct={r.successRatePct} />
          <span className="text-[10px] text-gray-500">
            {runs ? `${r.runsSucceeded}/${runs}` : 'no runs'}
          </span>
        </div>
        {r.agentCount ? (
          <span className="flex items-center gap-1 text-[10px] text-gray-500" title="Agents in this room">
            <Users className="h-3.5 w-3.5 text-indigo-500" aria-hidden />{r.agentCount}
          </span>
        ) : null}
        <div className="ml-auto flex min-w-0 items-center gap-1" title="Estimated cost consumed">
          <Server className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
          <ConsumptionMeter unit="usd" consumed={r.consumedPoints} consumedUsd={r.consumedUsd} budget={0} />
        </div>
      </div>

      {/* Primary CTA — the room-centric analog of "View N rooms": expand an INLINE
          overview of this room's executions right here on the landing (no navigation,
          so the user can peek into several rooms at once). The grid owns the panel. */}
      {runs ? (
        <button
          type="button"
          onClick={() => onToggleExecutions?.(r)}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-gray-100 py-1 text-[11px] font-medium text-gray-500 hover:bg-gray-50"
          data-testid={`room-landing-executions-${r.networkId}-${r.poolId}`}
          aria-expanded={!!expanded}
        >
          <History className="h-3.5 w-3.5" aria-hidden />
          {expanded ? 'Hide executions' : `View ${runs} execution${runs === 1 ? '' : 's'}`}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
        </button>
      ) : (
        <div className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-gray-100 py-1 text-[11px] font-medium text-gray-400"
             data-testid={`room-landing-executions-${r.networkId}-${r.poolId}`}>
          <History className="h-3.5 w-3.5" aria-hidden /> No executions yet
        </div>
      )}
    </motion.div>
  );
}
